import {
  IncrementalSource,
  MouseInteractions,
  type incrementalSnapshotEvent,
  type metaEvent,
} from '@rrweb/types';
import type { eventWithTime } from 'rrweb';

export type InteractionType = 'click' | 'scroll' | 'input';

export type InteractionEvent = {
  type: InteractionType;
  time: number;
  timestamp: number;
};

export type SessionStats = {
  viewport: string | null;
  duration: number;
  totalClicks: number;
  rageClicks: number;
  scrollEvents: number;
  inputEvents: number;
  idleTime: number;
};

// rrweb event type constants
const RRWEB_INCREMENTAL_SNAPSHOT_TYPE = 3;
const RRWEB_META_EVENT_TYPE = 4;

const SOURCE_TO_TYPE: Partial<Record<IncrementalSource, InteractionType>> = {
  [IncrementalSource.MouseInteraction]: 'click',
  [IncrementalSource.Scroll]: 'scroll',
  [IncrementalSource.Input]: 'input',
};

const RAGE_CLICK_THRESHOLD = 3;
const RAGE_CLICK_WINDOW_MS = 1000;
const IDLE_THRESHOLD_MS = 5000;

export const extractInteractions = (
  events: eventWithTime[],
): InteractionEvent[] => {
  if (events.length === 0) return [];

  const startTime = events[0].timestamp;
  const interactions: InteractionEvent[] = [];

  const isIncremental = (
    event: eventWithTime,
  ): event is incrementalSnapshotEvent & { timestamp: number } =>
    event.type === RRWEB_INCREMENTAL_SNAPSHOT_TYPE;

  for (const event of events) {
    if (!isIncremental(event)) continue;
    const { source } = event.data;

    if (source === IncrementalSource.MouseInteraction) {
      // rrweb groups all mouse interactions (MouseDown, MouseUp, Focus, Blur, etc.)
      // under one source — only count actual clicks.
      const mouseType = (event.data as { type: MouseInteractions }).type;
      if (mouseType === MouseInteractions.Click) {
        interactions.push({
          type: 'click',
          time: event.timestamp - startTime,
          timestamp: event.timestamp,
        });
      }
      continue;
    }

    const type = SOURCE_TO_TYPE[source];
    if (type) {
      interactions.push({
        type,
        time: event.timestamp - startTime,
        timestamp: event.timestamp,
      });
    }
  }

  return interactions;
};

export const countRageClicks = (clicks: InteractionEvent[]): number => {
  let rageClicks = 0;
  let cursor = 0;

  while (cursor < clicks.length) {
    const windowStart = clicks[cursor].timestamp;
    const windowEnd = windowStart + RAGE_CLICK_WINDOW_MS;

    // Find how many clicks fall within the window
    let end = cursor;
    while (end < clicks.length && clicks[end].timestamp <= windowEnd) {
      end++;
    }

    const clicksInWindow = end - cursor;

    if (clicksInWindow >= RAGE_CLICK_THRESHOLD) {
      rageClicks++;
      cursor = end; // skip past this burst
    } else {
      cursor++;
    }
  }

  return rageClicks;
};

export const computeIdleTime = (
  events: eventWithTime[],
  threshold = IDLE_THRESHOLD_MS,
): number => {
  let idleTime = 0;
  for (let i = 1; i < events.length; i++) {
    const gap = events[i].timestamp - events[i - 1].timestamp;
    if (gap > threshold) {
      idleTime += gap;
    }
  }
  return idleTime;
};

export const computeSessionStats = (events: eventWithTime[]): SessionStats => {
  if (events.length === 0) {
    return {
      viewport: null,
      duration: 0,
      totalClicks: 0,
      rageClicks: 0,
      scrollEvents: 0,
      inputEvents: 0,
      idleTime: 0,
    };
  }

  const startTime = events[0].timestamp;
  const endTime = events[events.length - 1].timestamp;

  const meta = events.find(
    (e): e is metaEvent & { timestamp: number } =>
      e.type === RRWEB_META_EVENT_TYPE,
  );
  const viewport =
    meta?.data.width && meta?.data.height
      ? `${meta.data.width}x${meta.data.height}`
      : null;

  const interactions = extractInteractions(events);
  const clicks = interactions.filter(i => i.type === 'click');

  return {
    viewport,
    duration: endTime - startTime,
    totalClicks: clicks.length,
    rageClicks: countRageClicks(clicks),
    scrollEvents: interactions.filter(i => i.type === 'scroll').length,
    inputEvents: interactions.filter(i => i.type === 'input').length,
    idleTime: computeIdleTime(events),
  };
};

export const formatTime = (ms: number, decimals = 0): string => {
  // Round to desired precision first, then split into minutes/seconds
  // to avoid "0:60.0" at minute boundaries (e.g. 59,950ms).
  const factor = 10 ** decimals;
  const roundedTotal = Math.round((ms / 1000) * factor) / factor;
  const minutes = Math.floor(roundedTotal / 60);
  const seconds = roundedTotal - minutes * 60;
  if (decimals === 0) {
    return `${minutes}:${Math.floor(seconds).toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toFixed(decimals).padStart(decimals + 3, '0')}`;
};

export const formatDuration = (ms: number): string => {
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return `${minutes}m ${remaining}s`;
};

export const INTERACTION_LABELS: Record<InteractionType, string> = {
  click: 'Click',
  scroll: 'Scroll',
  input: 'Input',
};
